// 1. Imports
import { Box, Grid2, Typography, Card, CardContent, IconButton } from "@mui/material";
import SendIcon from '@mui/icons-material/Send';
import RepeatIcon from '@mui/icons-material/Repeat';
import HistoryIcon from '@mui/icons-material/History';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import { useNavigate } from "react-router-dom";
import { store } from "../../../store";
import { permissionCheck } from "../../../helpers/permissionCheck";
import { customerPermissionFlags } from "../../../constants/systemPermissions.constants";
import { GT_COLORS } from "../../../constants/designTokens.constants";

// 2. Component Def
interface Props {}

export const StartCampaignComponent: React.FC<Props> = ({}) => {
    // 2.1 State and Vars
    const navigate = useNavigate();
    const canEdit = permissionCheck(customerPermissionFlags.TEAM_LEADER);

    // Mock data for the campaign cards
    const campaignCards = [
        {
            id: 'send-message',
            title: 'Send A Message',
            description: 'Manually send a one-time message to users, teams, or groups via push notification or SMS.',
            icon: <SendIcon sx={{ fontSize: '2rem', color: GT_COLORS.textMuted }} />,
            onClick: () => {
                // Clear the message center store before starting new message
                store.dispatch.messageCenter.resetAll();
                navigate('/messages/new/recipients');
            },
            disabled: !canEdit
        },
        {
            id: 'automated-reminders',
            title: 'Automated Reminders',
            description: 'Set up recurring messages based on user activity, such as inactivity alerts or device charging reminders.',
            icon: <RepeatIcon sx={{ fontSize: '2rem', color: GT_COLORS.textMuted }} />,
            onClick: () => {
                // Clear the message center store before starting new reminder
                store.dispatch.messageCenter.resetAll();
                navigate('/messages/automated-reminders/recipients');
            },
            disabled: !canEdit
        },
        {
            id: 'message-history',
            title: 'View Message History',
            description: 'View and track previously sent messages, including delivery status, recipient details, and message performance.',
            icon: <HistoryIcon sx={{ fontSize: '2rem', color: GT_COLORS.textMuted }} />,
            onClick: () => {
                navigate('/messages/history');
            },
            disabled: false
        }
    ];

    // 2.2 Effects

    // 2.3 Functions

    // 2.4 JSX Return
    return (
        <Box>
            {/* Section Title */}
            <Typography
                sx={{ fontSize: '1.5rem', fontWeight: 600, color: GT_COLORS.textPrimary, mb: 3 }}
            >
                Start A New Campaign
            </Typography>

            {/* Cards Grid */}
            <Grid2 container spacing={3}>
                {campaignCards.map((card) => (
                    <Grid2 size={4} key={card.id}>
                        <Card
                            elevation={0}
                            sx={{
                                height: '100%',
                                border: `1px solid ${GT_COLORS.border}`,
                                borderRadius: 2.5,
                                cursor: card.disabled ? 'default' : 'pointer',
                                opacity: card.disabled ? 0.6 : 1,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': !card.disabled ? {
                                    borderColor: GT_COLORS.primary,
                                    boxShadow: `0 4px 12px rgba(32, 111, 165, 0.12)`,
                                    transform: 'translateY(-2px)',
                                } : {}
                            }}
                            onClick={card.disabled ? undefined : card.onClick}
                        >
                            <CardContent sx={{ p: 3 }}>
                                {/* Icon */}
                                <Box sx={{ mb: 2 }}>
                                    {card.icon}
                                </Box>

                                {/* Title */}
                                <Typography
                                    sx={{
                                        fontSize: '1.125rem',
                                        fontWeight: 600,
                                        color: GT_COLORS.textPrimary,
                                        mb: 1,
                                    }}
                                >
                                    {card.title}
                                </Typography>

                                {/* Description */}
                                <Typography
                                    sx={{
                                        fontSize: '0.875rem',
                                        color: GT_COLORS.textMuted,
                                        mb: 2,
                                        minHeight: '3rem',
                                    }}
                                >
                                    {card.description}
                                </Typography>

                                {/* Arrow Icon */}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <IconButton
                                        disabled={card.disabled}
                                        sx={{
                                            color: card.disabled ? GT_COLORS.textDisabled : GT_COLORS.primary,
                                        }}
                                    >
                                        <KeyboardTabIcon />
                                    </IconButton>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid2>
                ))}
            </Grid2>
        </Box>
    );
}
